import {urls, api} from '@/api'
import * as types from '@/store/mutation-types'
import {sortMedia} from '@/helpers'

const getInitialState = () => ({
  id: null,
  title: '',
  body: '',
  categoryId: null,
  media: [],
  currencyCode: '',
  currencyValue: null,
  status: '',
  type: '',
  date: '',
  enableQuestions: null,
  attributes: [],
  categoryPath: [],
  phones: [],
  geopoint: null,
  locations: [],
  emails: [],
  user: {},
  userId: null,
  views: null,
  isFavorite: null,
  highlight: {},
  questions: [],
  questionsMaxLength: 255,
  questionsPagination: {
    current_page: 0,
    per_page: 6
  },
  relatedPubs: [],
  ads: [],
  forSale: false,
  salable: false,
  product: [],
})

const state = getInitialState()

const getters = {
  preview: (state) => ({
    ready: !!state.title,
    id: state.id,
    highlight: state.highlight && {
      highlight_band: state.highlight.highlight_band,
      highlight_types: state.highlight.highlight_types,
      rotating_images: state.highlight.rotating_images
    },
    date: state.date,
    category_path: state.categoryPath,
    media: state.media,
    title: state.title,
    currency: state.currencyCode,
    price: state.currencyValue,
    geopoint: state.geopoint,
    user: state.user,
    product: state.product,
  }),
}

const actions = {
  cleanSheet: ({commit}) => new Promise((resolve, reject) => {
    commit(types.PUB_RESET_STATE)
    resolve()
  }),

  getSheet: ({commit, dispatch}, id) => api.get(`${urls.publicationSheet}/${id}`)
    .then(({data}) => {
      dispatch('setSheet', {data, id})
    }),

  getUserSheet: ({commit, dispatch}, id) => api.get(`${urls.userPublicationSheet}/${id}`)
    .then(({data}) => {
      dispatch('setSheet', {data, id})
    }),

  setSheet: ({commit, dispatch}, {data, id}) => {
    commit(types.PUB_SET_TITLE, data.data.title)
    commit(types.PUB_SET_BODY, data.data.body)
    commit(types.PUB_SET_ID, data.data.publication_id)
    commit(types.PUB_SET_CATEGORY_ID, data.data.mla_category_id)
    commit(types.PUB_SET_CURRENCY_CODE, data.data.price.currency_code)
    commit(types.PUB_SET_CURRENCY_VALUE, data.data.price.currency_value)
    commit(types.PUB_SET_STATUS, data.data.status)
    commit(types.PUB_SET_TYPE, data.data.type)
    commit(types.PUB_SET_DATE, data.data.date)
    commit(types.PUB_SET_USER_ID, data.data.user_id)
    commit(types.PUB_SET_VIEWS, data.data.views)
    commit(types.PUB_SET_ENABLE_QUESTIONS, data.data.enable_questions)
    commit(types.PUB_SET_MEDIA, data.data.media)
    commit(types.PUB_SET_PHONES, data.data.phones)
    commit(types.PUB_SET_GEOPOINT, data.data.geopoint)
    commit(types.PUB_SET_LOCATIONS, data.data.locations)
    commit(types.PUB_SET_EMAILS, data.data.emails)
    commit(types.PUB_SET_ATTRIBUTES, data.data.attributes)
    commit(types.PUB_SET_CATEGORY_PATH, data.data.category_path)
    commit(types.PUB_SET_USER, data.data.user)
    commit(types.PUB_SET_HIGHLIGHT, data.data.highlight)
    commit(types.PUB_SET_FOR_SALE, data.data.for_sale)
    commit(types.PUB_SET_SALABLE, data.data.salable)
    if (data.data.product) commit(types.PUB_SET_PRODUCT, data.data.product) // FIXME

    return Promise.all([
      dispatch('profile/cleanProfile', null, { root: true })
        .then(() => dispatch('profile/fetchFollowers', null, { root: true })),
      data.data.enable_questions && dispatch('getQuestions', id)
    ])
  },

  fetchAds: ({commit, state}) => api.get(`${urls.ads}/sheet/${state.id}`)
    .then(({data}) => { commit(types.PUB_SET_ADS, data.data) })
    .catch(er => {}),

  getMoreQuestions: ({commit, state}, id = state.id) => {
    const params = {
      publication_id: id,
      page: state.questionsPagination.current_page + 1,
      per_page: state.questionsPagination.per_page
    }
    return api.get(urls.publicationQuestions, { params })
      .then(({data}) => {
        if (state.questions.length > 0) {
          commit(types.PUB_ADD_QUESTIONS, data.data)
        } else {
          commit(types.PUB_SET_QUESTIONS, data.data)
        }
        commit(types.PUB_SET_QUESTIONS_PAGINATION, data.meta && data.meta.pagination)
      })
  },

  getQuestions: ({commit, state}, id = state.id) => {
    const params = {
      publication_id: id,
      page: 1,
      per_page: state.questionsPagination.per_page
    }
    return api.get(urls.publicationQuestions, { params })
      .then(({data}) => {
        commit(types.PUB_SET_QUESTIONS, data.data)
        commit(types.PUB_SET_QUESTIONS_PAGINATION, data.meta && data.meta.pagination)
      })
  },

  createQuestion: ({commit, dispatch}, {id, question}) => {
    const params = { publication_id: id }
    const formData = { publication_id: id, question }

    return api.post(urls.publicationQuestions, formData, { params })
      .then(() => dispatch('getQuestions', id))
  },

  deleteQuestion: ({commit, dispatch}, {id, question}) => {
    return api.delete(`${urls.publicationQuestions}/${question}`)
      .then(() => { dispatch('getQuestions', id) })
      .catch((err) => { console.error('Error while deleting question: ', err) })
  },

  createAnswer: ({commit, dispatch}, {id, answer}) => {
    const formData = { question_id: id, answer }

    return api.post(urls.publicationAnswers, formData)
      .then(() => dispatch('getQuestions'))
  },

  fetchRelated: ({commit, state}, params = {}) => {
    params = {
      ...params,
      per_page: 8,
      page: 1
    }
    return api.get(`${urls.publications}/${state.id}/categories/${state.categoryId}/suggestions`, {params})
      .then(({data}) => { commit(types.PUB_SET_RELATED_PUBS, data.data) })
      .catch(er => {})
  },

  isFavorite: ({commit, state}) => api.get(`${urls.publications}/${state.id}/favorite`)
    .then(({data}) => {
      const fav = data.data === 'true'
      if (fav)
        commit(types.PUB_SET_FAVORITE, fav)
    })
    .catch((err) => {}),

  setFavorite: ({commit}, status) => { commit(types.PUB_SET_FAVORITE, status) },

  setStatus: ({commit}, status) => { commit(types.PUB_SET_STATUS, status) }
}

const mutations = {
  [types.PUB_RESET_STATE] (state) {
    Object.assign(state, getInitialState())
  },

  [types.PUB_SET_TITLE] (state, title) {
    state.title = title
  },

  [types.PUB_SET_BODY] (state, body) {
    state.body = body
  },

  [types.PUB_SET_ID] (state, id) {
    state.id = id
  },

  [types.PUB_SET_VIEWS] (state, views) {
    state.views = views
  },

  [types.PUB_SET_CATEGORY_ID] (state, categoryId) {
    state.categoryId = categoryId
  },

  [types.PUB_SET_CURRENCY_CODE] (state, currencyCode) {
    state.currencyCode = currencyCode
  },

  [types.PUB_SET_CURRENCY_VALUE] (state, currencyValue) {
    state.currencyValue = currencyValue
  },

  [types.PUB_SET_STATUS] (state, status) {
    state.status = status
  },

  [types.PUB_SET_TYPE] (state, type) {
    state.type = type
  },

  [types.PUB_SET_DATE] (state, date) {
    state.date = date
  },

  [types.PUB_SET_USER_ID] (state, userId) {
    state.userId = userId
  },

  [types.PUB_SET_ENABLE_QUESTIONS] (state, status) {
    state.enableQuestions = status
  },

  [types.PUB_SET_MEDIA] (state, media) {
    state.media = media || []
  },

  [types.PUB_SET_PHONES] (state, phones) {
    state.phones = phones || []
  },

  [types.PUB_SET_GEOPOINT] (state, geopoint) {
    state.geopoint = geopoint || null
  },

  [types.PUB_SET_LOCATIONS] (state, locations) {
    state.locations = locations || []
  },

  [types.PUB_SET_EMAILS] (state, emails) {
    state.emails = emails || []
  },

  [types.PUB_SET_ATTRIBUTES] (state, attributes) {
    state.attributes = attributes || []
  },

  [types.PUB_SET_CATEGORY_PATH] (state, categoryPath) {
    state.categoryPath = categoryPath || []
  },

  [types.PUB_SET_USER] (state, user) {
    state.user = user || {}
  },

  [types.PUB_SET_HIGHLIGHT] (state, hl) {
    state.highlight = hl || {}
  },

  [types.PUB_SET_QUESTIONS] (state, questions) {
    state.questions = questions || []
  },

  [types.PUB_ADD_QUESTIONS] (state, questions) {
    state.questions = state.questions.concat(questions)
  },

  [types.PUB_SET_QUESTIONS_PAGINATION] (state, pagination) {
    state.questionsPagination = pagination || {}
  },

  [types.PUB_SET_RELATED_PUBS] (state, pubs) {
    state.relatedPubs = pubs || []
  },

  [types.PUB_SET_ADS] (state, ads) {
    state.ads = ads || []
  },

  [types.PUB_SET_FAVORITE] (state, status) {
    state.isFavorite = status
  },

  [types.PUB_SET_FOR_SALE] (state, forSale) {
    state.forSale = forSale
  },

  [types.PUB_SET_SALABLE] (state, salable) {
    state.salable = salable
  },

  [types.PUB_SET_PRODUCT] (state, product) {
    state.product = product
  },

}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/publication.js